import {
  mdiAccount,
  mdiClose,
  mdiHome,
  mdiHomeCity,
  mdiMapMarker,
  mdiOfficeBuilding,
  mdiPhone,
  mdiPlus,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  ModalType,
  useModal,
} from "../../../../../context_providers/modal/modal_context";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { CustomerDetailModel } from "../../../../../service/models/orders/customer_detail_model";
import { GetBillingProfilesOfUserApi } from "../../../../../service/repos/customer/billing_profile_repo";
import { AddressType } from "../../../../../utils/enums";
import AddBillingProfileWidget from "./add_billing_profile_widget";
import { UpdateOrderDetailApi } from "../../../../../service/repos/order_repo";
import SelectAddressProfileWidget from "./select_address_profile_widget";
import { useAuth } from "../../../../../context_providers/auth/authProvider";

interface CustomerSectionWidgetProps {}

const BillingProfileWidget: FunctionComponent<
  CustomerSectionWidgetProps
> = () => {
  const { showModal, onClose } = useModal();
  const { showToast } = useToast();
  const { order, onCustomerSelect } = useOrderDetailCtx();
  const { user, hasRoleTitle } = useAuth();
  const [addNew, setAddNew] = useState(false);
  const [edit, setEdit] = useState(false);
  const [customers, setCustomers] = useState<CustomerDetailModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditAllowed, setIsEditAllowed] = useState(false);

  const getCustomers = async () => {
    setLoading(true);
    const res = await GetBillingProfilesOfUserApi(order.user_id);

    if (res.success) {
      setCustomers(res.data.billing_profiles);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (edit && order.user_id) getCustomers();
  }, [edit]);

  useEffect(() => {
    const loggedInUser = user?.uid;
    const orderCreatedBy = order.created_by;
    const hasOrderCreatorAccess = hasRoleTitle({
      roleTitle: "Create orders only",
    });

    // Allow edit if the logged in user created the order, or has order creator role
    if (
      (loggedInUser === orderCreatedBy || hasOrderCreatorAccess) &&
      order?.update_restrict !== true
    ) {
      setIsEditAllowed(true);
    } else {
      setIsEditAllowed(false);
    }
  }, [user, order]);
  return (
    <div className="bg-white   rounded-md text-sm  h-full">
      {order.customer && !edit ? (
        <div className="p-5">
          <div className="flex justify-end">
            {isEditAllowed && (
              <button
                onClick={() => {
                  showModal({
                    title: "Select Billing Profile",
                    type: ModalType.modal,
                    container: (id) => (
                      <SelectAddressProfileWidget
                        type="Billing"
                        user_id={order.user_id}
                        selected_id={order.customer?.id}
                        onAdded={async (data) => {
                          const billingAddress = {
                            branch_name: data.address.branch_name ?? "",
                            type: data.address.type ?? "Billing",
                            line1: data.address.line1 ?? "",
                            line2: data.address.line2 ?? "",
                            line3: data.address.line3 ?? "",
                            city: data.address.city ?? "",
                            state: data.address.state ?? "",
                            country: data.address.country ?? "",
                            pincode: data.address.pincode ?? 0,
                          };

                          // 1. Update UI immediately
                          const updatedCustomer = {
                            ...order.customer,
                            billing_address: [billingAddress],
                            id: data.id,
                          };

                          // 2. Call UpdateOrderDetailApi to persist this change in DB
                          await UpdateOrderDetailApi({
                            order_id: order?.order_id,
                            customer: updatedCustomer,
                          });

                          onCustomerSelect(updatedCustomer);

                          // 3. Close modal and show toast
                          onClose(id);
                          showToast({
                            type: ToastType.success,
                            text: "Billing address updated in order",
                          });
                        }}
                      />
                    ),
                  });
                }}
                className="cursor-pointer hover:scale-125 transform duration-300 text-myPrimaryColor font-semibold"
              >
                Edit
              </button>
            )}
          </div>
          <div className="">
            <div className="flex justify-between">
              <div className="">
                {" "}
                <div className="flex gap-1">
                  <span>
                    <Icon
                      path={
                        order.customer?.address?.type === AddressType.HOME
                          ? mdiHome
                          : order.customer?.address?.type === AddressType.OFFICE
                            ? mdiOfficeBuilding
                            : mdiHomeCity
                      }
                      className="h-4 w-4 text-gray-500"
                    ></Icon>
                  </span>
                  <p>
                    <b>Billing</b>
                  </p>
                </div>
                <div className="flex gap-1">
                  <span>
                    <Icon
                      path={mdiAccount}
                      className="h-4 w-4 text-gray-500"
                    ></Icon>
                  </span>
                  <p>
                    {(order?.customer?.firstName ||
                      order?.customer?.first_name ||
                      "") +
                      " " +
                      (order?.customer?.lastName ||
                        order?.customer?.last_name ||
                        "")}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Icon
                    path={mdiPhone}
                    className="h-4 w-4 text-gray-500"
                  ></Icon>

                  <p>
                    {order.customer?.mobile}{" "}
                    {order.customer?.alt_mobile && (
                      <span>, {order.customer?.alt_mobile}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-xs">
                <div>
                  Branch:{" "}
                  {order?.billing_profile?.branch_name ??
                    order?.billing_profile?.address?.branch_name ??
                    "--"}
                </div>
                <div className="flex gap-1">
                  GST :
                  <p>
                    {order.customer?.gst ??
                      order.customer?.gst_details?.gst_number ??
                      "--"}
                  </p>
                </div>
                <div className="flex gap-1">
                  CA :<p>{order.customer.ca ?? "--"}</p>
                </div>
              </div>
            </div>
            <hr className="my-2" />
            <div className="flex gap-1">
              <Icon
                path={mdiMapMarker}
                size={1}
                className="  text-gray-500"
              ></Icon>
              <p className="text-xs">
                {order?.billing_profile?.line1 ??
                  order?.billing_profile?.address?.line1 ??
                  order.customer?.billing_address?.[0]?.line1}
                ,{" "}
                {order?.billing_profile?.line2 ??
                  order?.billing_profile?.address?.line2 ??
                  order.customer?.address?.line2 ??
                  order.customer?.billing_address?.[0].line2}
                ,{" "}
                {order?.billing_profile?.city ??
                  order?.billing_profile?.address?.city ??
                  order.customer?.address?.city ??
                  order.customer?.billing_address?.[0].city}
                ,{" "}
                {order?.billing_profile?.state ??
                  order?.billing_profile?.address?.state ??
                  order.customer?.address?.state ??
                  order.customer?.billing_address?.[0].state}
                ,{" "}
                {order?.billing_profile?.country ??
                  order?.billing_profile?.address?.country ??
                  order.customer?.address?.country ??
                  order.customer?.billing_address?.[0].country}{" "}
                -{" "}
                {order?.billing_profile?.pincode ??
                  order?.billing_profile?.address?.pincode ??
                  order.customer?.address?.pincode ??
                  order.customer?.billing_address?.[0].pincode}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="">
          <div className="flex justify-end gap-2 p-5">
            <button
              onClick={() => {
                if (!order.user_id) {
                  showToast({
                    type: ToastType.info,
                    text: "Please select customer ",
                  });
                  return;
                }
                setAddNew(true);
                showModal({
                  container: (id) => (
                    <AddBillingProfileWidget
                      address_type="bill"
                      type={order.type}
                      user_id={order.user_id}
                      onAdded={(data) => {
                        onCustomerSelect(data);
                        setEdit(false);
                      }}
                    />
                  ),

                  type: ModalType.modal,
                  title: "Add new billing profile",
                });
              }}
              className="cursor-pointer   text-myPrimaryColor font-semibold p-1 bg-green-100 rounded"
            >
              <Icon path={mdiPlus} className="h-4 w-4"></Icon>
            </button>
            {edit && (
              <button
                onClick={() => {
                  setEdit(false);
                }}
                className="cursor-pointer   text-red-400 font-semibold p-1 bg-red-100 rounded"
              >
                <Icon path={mdiClose} className="h-4 w-4"></Icon>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingProfileWidget;
